'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

type Variant = 'dead-pixel' | 'screen-test' | 'stuck-pixel' | 'refresh-rate';

const COLORS: Array<[string, string]> = [
  ['#000000', 'Black: bright dots are stuck or hot pixels'],
  ['#ffffff', 'White: dark dots are dead pixels'],
  ['#ff0000', 'Red'],
  ['#00ff00', 'Green'],
  ['#0000ff', 'Blue'],
  ['#00ffff', 'Cyan'],
  ['#ff00ff', 'Magenta'],
  ['#ffff00', 'Yellow'],
  ['#808080', 'Gray 50%: look for uneven patches and banding'],
];

type Pattern = {
  name: string;
  hint: string;
  paint: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
};

const PATTERNS: Pattern[] = [
  {
    name: 'Gradient',
    hint: 'Smooth ramps. Visible stripes (banding) mean low colour depth or heavy compression.',
    paint: (ctx, w, h) => {
      const rows: Array<[string, string]> = [
        ['#000', '#fff'],
        ['#000', '#f00'],
        ['#000', '#0f0'],
        ['#000', '#00f'],
      ];
      rows.forEach(([a, b], i) => {
        const g = ctx.createLinearGradient(0, 0, w, 0);
        g.addColorStop(0, a);
        g.addColorStop(1, b);
        ctx.fillStyle = g;
        ctx.fillRect(0, (h / 4) * i, w, h / 4);
      });
    },
  },
  {
    name: 'Black levels',
    hint: 'Squares from 0 to 10% gray. You should tell at least the last 7 apart from black; if not, lower brightness or check gamma.',
    paint: (ctx, w, h) => {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, w, h);
      const n = 11;
      const size = Math.min(w / (n + 1), h / 3);
      for (let i = 0; i < n; i += 1) {
        const v = Math.round((i / 100) * 255);
        ctx.fillStyle = `rgb(${v},${v},${v})`;
        ctx.fillRect((w - n * size) / 2 + i * size, h / 2 - size / 2, size * 0.9, size);
        ctx.fillStyle = '#555';
        ctx.font = `${Math.max(10, size / 5)}px system-ui`;
        ctx.fillText(`${i}%`, (w - n * size) / 2 + i * size + 4, h / 2 + size);
      }
    },
  },
  {
    name: 'White levels',
    hint: 'Squares from 90 to 100% gray. Every step should be distinguishable from white; if not, contrast is set too high.',
    paint: (ctx, w, h) => {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      const n = 11;
      const size = Math.min(w / (n + 1), h / 3);
      for (let i = 0; i < n; i += 1) {
        const v = Math.round(((90 + i) / 100) * 255);
        ctx.fillStyle = `rgb(${v},${v},${v})`;
        ctx.fillRect((w - n * size) / 2 + i * size, h / 2 - size / 2, size * 0.9, size);
      }
    },
  },
  {
    name: 'Sharpness grid',
    hint: 'One-pixel lines and checkerboards. Blur or moiré mean the screen is not at its native resolution or scaling is on.',
    paint: (ctx, w, h) => {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, w, h);
      const img = ctx.getImageData(0, 0, w, h);
      for (let y = 0; y < h; y += 1)
        for (let x = 0; x < w; x += 1) {
          const zone = Math.floor((x / w) * 3);
          const on = zone === 0 ? (x + y) % 2 === 0 : zone === 1 ? x % 2 === 0 : y % 2 === 0;
          if (on) {
            const k = (y * w + x) * 4;
            img.data[k] = img.data[k + 1] = img.data[k + 2] = 0;
          }
        }
      ctx.putImageData(img, 0, 0);
    },
  },
  {
    name: 'Uniformity',
    hint: 'Dark gray field. Brighter corners or clouds are backlight bleed or clouding, common on IPS and VA panels.',
    paint: (ctx, w, h) => {
      ctx.fillStyle = '#202020';
      ctx.fillRect(0, 0, w, h);
    },
  },
];

function useFullscreen(ref: React.RefObject<HTMLDivElement | null>) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const sync = () => setOn(document.fullscreenElement === ref.current);
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [ref]);
  const enter = useCallback(async () => {
    try {
      await ref.current?.requestFullscreen();
    } catch {
      // iPhone Safari has no element fullscreen; the in-page view still works.
    }
  }, [ref]);
  return { on, enter };
}

function ColorScreens({ patterns, report }: { patterns: boolean; report: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const { on, enter } = useFullscreen(box);
  const total = COLORS.length + (patterns ? PATTERNS.length : 0);
  const [i, setI] = useState(0);
  const [hideHint, setHideHint] = useState(false);
  const isPattern = i >= COLORS.length;
  const pattern = isPattern ? PATTERNS[i - COLORS.length] : null;

  useEffect(() => {
    if (!pattern || !canvas.current) return;
    const c = canvas.current;
    const dpr = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = Math.round(rect.width * dpr);
    c.height = Math.round(rect.height * dpr);
    const ctx = c.getContext('2d');
    if (ctx) pattern.paint(ctx, c.width, c.height);
  }, [pattern, on]);

  const go = useCallback((d: number) => setI((v) => (v + d + total) % total), [total]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!on) return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        go(1);
      }
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'h') setHideHint((v) => !v);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [on, go]);

  const [color, label] = isPattern ? ['#000', pattern?.name ?? ''] : COLORS[i];
  return (
    <>
      <div
        onClick={() => {
          if (on) go(1);
        }}
        ref={box}
        style={{
          position: 'relative',
          background: color,
          height: on ? '100vh' : 260,
          borderRadius: on ? 0 : 12,
          border: on ? 0 : '1px solid rgba(127,127,127,0.4)',
          overflow: 'hidden',
          cursor: on ? 'none' : 'default',
        }}
      >
        {isPattern ? (
          <canvas ref={canvas} style={{ width: '100%', height: '100%', display: 'block' }} />
        ) : null}
        {!hideHint ? (
          <div
            style={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              right: 12,
              padding: '0.4rem 0.6rem',
              borderRadius: 6,
              background: 'rgba(0,0,0,0.6)',
              color: '#fff',
              fontSize: '0.85rem',
            }}
          >
            {i + 1}/{total} · {isPattern ? pattern?.hint : label}
            {on ? ' · Click or → for next, ← back, H hides this, Esc exits' : ''}
          </div>
        ) : null}
      </div>
      <div className="tool-live-actions">
        <button
          className="button"
          onClick={() => {
            report();
            void enter();
          }}
          type="button"
        >
          Start full-screen test
        </button>
        <button className="button button-secondary" onClick={() => go(-1)} type="button">
          Previous
        </button>
        <button className="button button-secondary" onClick={() => go(1)} type="button">
          Next
        </button>
      </div>
    </>
  );
}

function StuckPixelFixer({ report }: { report: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const { on, enter } = useFullscreen(box);
  const [agreed, setAgreed] = useState(false);
  const [running, setRunning] = useState(false);
  const [size, setSize] = useState(120);
  const [pos, setPos] = useState({ x: 40, y: 40 });
  const [elapsed, setElapsed] = useState(0);
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  useEffect(() => {
    if (!running) return;
    let raf = 0;
    const t0 = performance.now();
    const loop = () => {
      const c = canvas.current;
      const ctx = c?.getContext('2d');
      if (c && ctx) {
        const img = ctx.createImageData(c.width, c.height);
        for (let k = 0; k < img.data.length; k += 4) {
          // Random primaries and black, redrawn every frame, exercise each subpixel on/off.
          const r = Math.random();
          img.data[k] = r < 0.25 || r > 0.75 ? 255 : 0;
          img.data[k + 1] = r >= 0.25 && r < 0.5 ? 255 : r > 0.75 ? 255 : 0;
          img.data[k + 2] = r >= 0.5 ? 255 : 0;
          img.data[k + 3] = 255;
        }
        ctx.putImageData(img, 0, 0);
      }
      setElapsed(Math.floor((performance.now() - t0) / 1000));
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [running]);

  return (
    <>
      {!agreed ? (
        <div
          className="tool-live-panel-note"
          role="alert"
          style={{ border: '2px solid #dc2626', borderRadius: 8, padding: '0.75rem' }}
        >
          <strong>Photosensitivity warning.</strong> This tool flashes rapidly changing colours. Do
          not use it if you or anyone watching has photosensitive epilepsy or is sensitive to
          flashing light. Look away from the screen while it runs.
          <div className="tool-live-actions">
            <button className="button" onClick={() => setAgreed(true)} type="button">
              I understand, show the fixer
            </button>
          </div>
        </div>
      ) : (
        <>
          <div
            onPointerMove={(e) => {
              if (!drag.current || !box.current) return;
              const r = box.current.getBoundingClientRect();
              setPos({
                x: Math.min(Math.max(0, e.clientX - r.left - drag.current.dx), r.width - size),
                y: Math.min(Math.max(0, e.clientY - r.top - drag.current.dy), r.height - size),
              });
            }}
            onPointerUp={() => (drag.current = null)}
            ref={box}
            style={{
              position: 'relative',
              background: '#000',
              height: on ? '100vh' : 300,
              borderRadius: on ? 0 : 12,
              overflow: 'hidden',
              touchAction: 'none',
            }}
          >
            <canvas
              height={Math.max(8, Math.round(size / 4))}
              onPointerDown={(e) => {
                const r = (e.target as HTMLElement).getBoundingClientRect();
                drag.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
                (e.target as HTMLElement).setPointerCapture(e.pointerId);
              }}
              ref={canvas}
              style={{
                position: 'absolute',
                left: pos.x,
                top: pos.y,
                width: size,
                height: size,
                imageRendering: 'pixelated',
                cursor: 'move',
                outline: running ? 'none' : '2px dashed #fff',
                background: '#333',
              }}
              width={Math.max(8, Math.round(size / 4))}
            />
            {!running ? (
              <p
                style={{
                  position: 'absolute',
                  left: 12,
                  bottom: 8,
                  right: 12,
                  color: '#fff',
                  fontSize: '0.85rem',
                }}
              >
                Drag the square over the stuck pixel, then press Start.
              </p>
            ) : null}
          </div>
          <div className="tool-live-actions">
            <button
              className="button"
              onClick={() => {
                report();
                setRunning((v) => !v);
              }}
              type="button"
            >
              {running
                ? `Stop (${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')})`
                : 'Start flashing'}
            </button>
            <button className="button button-secondary" onClick={() => void enter()} type="button">
              Full screen
            </button>
            <label className="tool-live-field" style={{ minWidth: 160 }}>
              <span>Square size: {size}px</span>
              <input
                max={400}
                min={40}
                onChange={(e) => setSize(Number(e.target.value))}
                type="range"
                value={size}
              />
            </label>
          </div>
        </>
      )}
    </>
  );
}

const LANES = [1, 2, 4];

function RefreshRate({ report }: { report: () => void }) {
  const [hz, setHz] = useState<number | null>(null);
  const [jitter, setJitter] = useState<number | null>(null);
  const [speed, setSpeed] = useState(960);
  const track = useRef<HTMLCanvasElement>(null);

  // Measure: median of requestAnimationFrame intervals over a rolling second.
  useEffect(() => {
    let raf = 0;
    let prev = 0;
    const gaps: number[] = [];
    const pos = LANES.map(() => 0);
    const pending = LANES.map(() => 0);
    let frame = 0;
    let lastPaint = 0;
    const loop = (t: number) => {
      if (prev) {
        gaps.push(t - prev);
        if (gaps.length > 120) gaps.shift();
      }
      prev = t;
      if (gaps.length >= 30 && gaps.length % 15 === 0) {
        const sorted = [...gaps].sort((a, b) => a - b);
        const med = sorted[Math.floor(sorted.length / 2)];
        setHz(Math.round(1000 / med));
        const late = gaps.filter((g) => g > med * 1.5).length;
        setJitter(Math.round((late / gaps.length) * 100));
      }
      const c = track.current;
      const ctx = c?.getContext('2d');
      if (c && ctx) {
        const dt = lastPaint ? Math.min(0.1, (t - lastPaint) / 1000) : 0;
        lastPaint = t;
        frame += 1;
        const laneH = c.height / LANES.length;
        ctx.fillStyle = '#111';
        ctx.fillRect(0, 0, c.width, c.height);
        LANES.forEach((div, i) => {
          // Lane i moves only every div-th frame (by the distance of all the frames it
          // skipped), which is exactly how the same motion looks at 1/div of the rate.
          pending[i] += speed * dt;
          if (frame % div === 0) {
            pos[i] = (pos[i] + pending[i]) % (c.width + 40);
            pending[i] = 0;
          }
          ctx.fillStyle = ['#38bdf8', '#a3e635', '#f472b6'][i];
          ctx.fillRect(pos[i] - 40, i * laneH + laneH * 0.3, 40, laneH * 0.4);
          ctx.fillStyle = '#9ca3af';
          ctx.font = '12px system-ui';
          ctx.fillText(div === 1 ? 'Full rate' : `1/${div} rate`, 8, i * laneH + 14);
        });
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [speed]);

  return (
    <>
      <canvas
        height={180}
        ref={track}
        style={{ width: '100%', height: 180, borderRadius: 12, display: 'block' }}
        width={960}
      />
      <div className="tool-live-actions">
        <label className="tool-live-field" style={{ minWidth: 200 }}>
          <span>Speed: {speed} px/s</span>
          <input
            max={1920}
            min={240}
            onChange={(e) => {
              report();
              setSpeed(Number(e.target.value));
            }}
            step={240}
            type="range"
            value={speed}
          />
        </label>
      </div>
      <div className="tool-html-meta-grid">
        {[
          ['Refresh rate', hz ? `${hz} Hz` : 'Measuring…'],
          ['Frame time', hz ? `${(1000 / hz).toFixed(2)} ms` : '—'],
          ['Late frames', jitter === null ? '—' : `${jitter}%`],
        ].map(([k, v]) => (
          <div className="tool-live-field" key={k}>
            <span>{k}</span>
            <input readOnly type="text" value={v} />
          </div>
        ))}
      </div>
    </>
  );
}

export default function BrowserScreenTest({
  config,
}: {
  config?: { toolSlug?: string; variant?: string; heading?: string };
}) {
  const variant = (config?.variant ?? 'dead-pixel') as Variant;
  const reported = useRef(false);
  const report = useCallback(() => {
    if (reported.current) return;
    reported.current = true;
    void consumeToolUsage(config?.toolSlug ?? 'dead-pixel-test', '').catch(() => undefined);
  }, [config?.toolSlug]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Display</span>
        <h2 className="tool-live-title">{config?.heading ?? 'Dead pixel test'}</h2>
        <p className="tool-live-subtitle">
          {variant === 'refresh-rate'
            ? 'Your screen’s refresh rate, measured live, and a motion test that shows what it looks like.'
            : variant === 'stuck-pixel'
              ? 'Flashes colours over a stuck pixel to try to unstick it.'
              : 'Full-screen colours and patterns. Clean the screen first so dust is not mistaken for a pixel.'}
        </p>
      </div>
      <div className="tool-live-grid" style={{ gridTemplateColumns: '1fr' }}>
        <section className="tool-live-preview">
          {variant === 'refresh-rate' ? (
            <RefreshRate report={report} />
          ) : variant === 'stuck-pixel' ? (
            <StuckPixelFixer report={report} />
          ) : (
            <ColorScreens patterns={variant === 'screen-test'} report={report} />
          )}
          <p className="tool-live-panel-note">
            {variant === 'refresh-rate'
              ? 'The browser draws once per screen refresh, so the frame interval reveals the rate. On a 120 Hz or 144 Hz screen, a lower number means the OS, battery saver or the cable is limiting it. The three lanes show the same motion at full, half and quarter rate.'
              : variant === 'stuck-pixel'
                ? 'Run it for 10-20 minutes. It revives stuck pixels (lit in one colour) in some cases; it cannot fix dead pixels (always black), which are a hardware fault.'
                : 'A dot that stays black on every colour is dead. One that stays lit in one colour is stuck and may respond to the stuck pixel fixer.'}
          </p>
        </section>
      </div>
    </div>
  );
}
